// TutorsProgramWorkloadBar.jsx
import React, { useMemo } from "react";
import {
  Box,
  Stack,
  Typography,
  Divider,
  useTheme,
} from "@mui/material";
import { BarChart } from "@mui/x-charts/BarChart";

export default function TutorsProgramWorkloadBar({ data }) {
  const theme = useTheme();
  const rows = Array.isArray(data) ? data : [];

  // 1. Transform data with useMemo for performance
  const chartDataset = useMemo(() => {
    return [...rows] 
      .sort((a, b) => b.duration_sum - a.duration_sum)
      .map((item) => {
        const fullName = `${item.first_name.trim()} ${item.last_name.trim()}`;
        const programName = item.program;
        
        return {
          label: `${fullName} (${programName})`.length > 30 
            ? `${fullName.substring(0, 15)}... \n (${programName})` 
            : `${fullName} \n (${programName})`,
          minutes: item.duration_sum || 0,
        };
      });
  }, [rows]);

  // 2. Format labels for X-axis and Tooltips
  const formatTimeLabel = (mins) => {
    if (mins >= 60) {
      return `${(mins / 60).toFixed(1)} hrs`;
    }
    return `${mins} mins`;
  };

  // 3. Calculate dynamic height (approx 60px per bar + margins)
  const chartHeight = Math.max(300, chartDataset.length * 60);

  return (
    <Box sx={{ p: 2}}>
      <Box sx={{ mb: 0 }}>
        <Stack direction="row" spacing={1} sx={{ display: "flex", alignItems: "center" }}>
          <Box>
            <Typography variant="subtitle1" fontWeight="bold">
              Tutor Workload by Program
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Total teaching time (hours/minutes) per program
            </Typography>
          </Box>
        </Stack>
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ width: '100%', height: chartHeight }}>
        <BarChart
          dataset={chartDataset}
          layout="horizontal"
          yAxis={[
            {
              scaleType: 'band',
              dataKey: 'label',
              tickLabelStyle: {
                angle: 0,
                textAnchor: 'end',
                fontSize: 12
              }
            },
          ]}
          
          xAxis={[
            {
              label: 'Duration',
              valueFormatter: (val) => formatTimeLabel(val),
              tickLabelStyle: {
                angle: 0,
                textAnchor: 'end',
                fontSize: 12
              }
            },
          ]}
          series={[
            {
              dataKey: 'minutes',
              label: 'Time Logged',
              // Using a gradient-like color or theme primary
              color: theme.palette.primary.main,
              valueFormatter: (value) => formatTimeLabel(value),
            },
          ]}
          // Adjust margins so labels have room to breathe
          margin={{ left: 100, right: 0, top: 10, bottom: 50 }}
          grid={{ vertical: true }}
          slotProps={{
            legend: { hidden: true }
          }}
          
        />
      </Box>
    </Box>
  );
}